import { datadogRum } from '@datadog/browser-rum';
import { toast } from '@side/pantry';

export type GooglePlacesResult = {
  mlsServiceId?: string | null;
  mlsNumber?: string | null;
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  county?: string | null;
};

type AutocompleteDictionary = Record<
  string,
  {
    key: string;
    type: string;
  }
>;

class GeocoderError extends Error {
  constructor(
    message: string,
    public readonly status: google.maps.GeocoderStatusString,
  ) {
    super(message);
    this.name = 'GeocoderError';
  }
}

const IGNORED_GEOCODER_ERRORS: google.maps.GeocoderStatusString[] = [
  'ERROR',
  'UNKNOWN_ERROR',
  'ZERO_RESULTS',
];

export function handleAddressDetailsError(error: unknown) {
  const shouldLogError =
    error instanceof GeocoderError
      ? !IGNORED_GEOCODER_ERRORS.includes(error.status)
      : true;

  if (shouldLogError) {
    datadogRum.addError(error);
  }

  toast(
    'error',
    'Failed to get address details. Please try again or enter the address manually.',
  );
}

const stateBoundsCache = new Map<
  string,
  Promise<google.maps.LatLngBounds | undefined>
>();

/**
 * Google Places has no state-level restriction, so the state's own viewport is
 * what keeps predictions inside it — `locationRestriction` takes a rectangle,
 * which is what a viewport is. The rectangle overhangs neighbouring states, so
 * callers still have to filter the predictions they get back.
 *
 * A failed lookup resolves to `undefined` so the search falls back to an
 * unrestricted one rather than returning nothing, and is evicted from the cache
 * so a transient geocoder error doesn't degrade the whole session.
 */
export function getStateBounds(
  state: string,
): Promise<google.maps.LatLngBounds | undefined> {
  const cacheKey = state.toUpperCase();
  const cached = stateBoundsCache.get(cacheKey);

  if (cached) return cached;

  const bounds = new Promise<google.maps.LatLngBounds | undefined>(
    (resolve) => {
      if (!window.google?.maps?.Geocoder) {
        resolve(undefined);
        return;
      }

      void new window.google.maps.Geocoder().geocode(
        { address: `${state}, USA` },
        (response, status) => {
          resolve(
            status === window.google.maps.GeocoderStatus.OK
              ? response?.[0]?.geometry.viewport
              : undefined,
          );
        },
      );
    },
  );

  stateBoundsCache.set(cacheKey, bounds);
  void bounds.then((viewport) => {
    if (!viewport) stateBoundsCache.delete(cacheKey);
  });

  return bounds;
}

// When we get back an address from the places autocomplete, it's not in a guaranteed
// shape to where we can get our address details off of it. It also doesn't return
// back the county of the address. Because of this, we need to use the Geocoder API
// to get back all of the information necessary in a guaranteed format. I wish there
// was a simpler way but unfortunately there isn't.
export async function getAddressDetails(
  place: google.maps.places.AutocompletePrediction,
): Promise<GooglePlacesResult> {
  return new Promise((resolve, reject) => {
    if (!place) {
      reject(new Error('No place selected.'));
      return;
    }
    if (!window.google?.maps) {
      reject(new Error('Google Maps SDK not loaded.'));
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    const request = { placeId: place.place_id };
    void geocoder.geocode(
      request,
      (response: google.maps.GeocoderResult[] | null, status) => {
        if (status !== window.google.maps.GeocoderStatus.OK) {
          reject(
            new GeocoderError(
              'Could not get place details for placeId',
              status,
            ),
          );
          return;
        }

        if (response !== null) {
          const placeDetails = { ...place, ...response[0] };
          // In some cases, structured_formatting.main_text is just the street number of
          // the address as opposed to being the street number and street name.
          // This will check to see if that is true. If it is, get the address off of the description otherwise
          // get it from structured_formatting.main_text.
          const streetNumber = placeDetails.address_components?.[0]?.short_name;
          const street1 =
            placeDetails.structured_formatting?.main_text === streetNumber
              ? placeDetails.description?.split(',')[0]
              : placeDetails.structured_formatting?.main_text;

          const dictionary: AutocompleteDictionary = {
            administrative_area_level_2: {
              key: 'short_name',
              type: 'county',
            },
            administrative_area_level_1: {
              key: 'short_name',
              type: 'state',
            },
            locality: { key: 'long_name', type: 'city' },
            postal_code: { key: 'short_name', type: 'zip' },
          };

          const addressComponents = placeDetails.address_components.reduce(
            (
              acc: GooglePlacesResult,
              component: google.maps.GeocoderAddressComponent,
            ) => {
              const componentType = component.types.find(
                (type) => !!dictionary[type],
              );
              if (!componentType) {
                return acc;
              }
              const { type, key } = dictionary[componentType];
              const componentValue =
                component[key as keyof google.maps.GeocoderAddressComponent];
              acc[type as keyof GooglePlacesResult] = componentValue as string;
              return acc;
            },
            {
              street1,
              county: '',
              city: '',
              state: '',
              zip: '',
            },
          );

          // If there isn't a locality type to specify the city, we can try getting
          // the city off of the sublocality or neighborhood type.
          if (!addressComponents.city) {
            const neighborhood =
              placeDetails.address_components.find((component) =>
                component.types.includes('neighborhood'),
              )?.long_name ?? '';
            const sublocality =
              placeDetails.address_components.find((component) =>
                component.types.includes('sublocality'),
              )?.long_name ?? '';

            // Sublocality should take precendence over neighborhood.
            addressComponents.city = sublocality ?? neighborhood ?? '';
          }

          resolve(addressComponents);
        }
      },
    );
  });
}
